import { useState, useEffect } from 'react';
import axios from 'axios';
import { getLang } from '../../utils/i18n';
import SetAlertModal from '../Alerts/SetAlertModal';
import { getToken } from '../../utils/auth';

const api = axios.create({ baseURL: '/api', timeout: 60000 });
api.interceptors.request.use(cfg => { const t = getToken(); if (t) cfg.headers.Authorization = `Bearer ${t}`; return cfg; });

const fmt = (n) => n != null ? `$${Number(n).toFixed(2)}` : '—';
const fmtR = (n) => n != null ? `$${Number(n).toFixed(2)}` : '—';

function confidenceColor(c) {
  if (c === 'high') return 'var(--green)';
  if (c === 'medium') return 'var(--yellow)';
  return 'var(--red)';
}

function RecommendationBadge({ recommendation, confidence, suitableForTrade }) {
  const isLong = recommendation === 'long';
  const isShort = recommendation === 'short';
  const isAside = recommendation === 'stand_aside';
  const bg = isLong ? 'var(--green-dim)' : isShort ? 'var(--red-dim)' : 'rgba(156,163,175,0.15)';
  const border = isLong ? 'var(--green)' : isShort ? 'var(--red)' : 'var(--border)';
  const color = isLong ? 'var(--green)' : isShort ? 'var(--red)' : 'var(--text-muted)';
  const label = isLong ? '↑ LONG' : isShort ? '↓ SHORT' : '⏸ STAND ASIDE';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <div style={{
        fontSize: 20, fontWeight: 800, letterSpacing: '-0.5px',
        padding: '6px 16px', borderRadius: 8,
        background: bg, border: `2px solid ${border}`, color,
      }}>
        {label}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Confidence:</span>
        <span style={{
          fontSize: 12, fontWeight: 700, padding: '2px 10px', borderRadius: 20,
          background: confidenceColor(confidence) + '22',
          border: `1px solid ${confidenceColor(confidence)}`,
          color: confidenceColor(confidence),
          textTransform: 'capitalize',
        }}>{confidence}</span>
        {!suitableForTrade && (
          <span style={{ fontSize: 12, color: 'var(--yellow)', fontWeight: 600 }}>⚠ Low probability setup</span>
        )}
      </div>
    </div>
  );
}

function PriceLadder({ setup, direction, onSetAlert }) {
  const isLong = direction === 'long';
  const { entry, stopLoss, takeProfit2R, takeProfit3R, riskR } = setup;
  if (!entry) return null;

  const rows = [
    { label: '3R Take Profit', price: takeProfit3R, color: 'var(--green)', bg: 'var(--green-dim)', icon: '🎯' },
    { label: '2R Take Profit', price: takeProfit2R, color: '#86efac', bg: 'rgba(134,239,172,0.1)', icon: '✅' },
    { label: 'Entry Zone', price: entry, color: 'var(--accent)', bg: 'var(--accent-dim)', icon: '⮕', bold: true, isEntry: true },
    { label: 'Stop Loss (1R)', price: stopLoss, color: 'var(--red)', bg: 'var(--red-dim)', icon: '🛑' },
  ];

  const displayRows = isLong ? rows : [
    { label: 'Stop Loss (1R)', price: stopLoss, color: 'var(--red)', bg: 'var(--red-dim)', icon: '🛑' },
    { label: 'Entry Zone', price: entry, color: 'var(--accent)', bg: 'var(--accent-dim)', icon: '⮕', bold: true, isEntry: true },
    { label: '2R Take Profit', price: takeProfit2R, color: '#86efac', bg: 'rgba(134,239,172,0.1)', icon: '✅' },
    { label: '3R Take Profit', price: takeProfit3R, color: 'var(--green)', bg: 'var(--green-dim)', icon: '🎯' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {displayRows.map((r, i) => (
        <div key={i} style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 14px', borderRadius: 8,
          background: r.bg, border: `1px solid ${r.color}44`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 14 }}>{r.icon}</span>
            <span style={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: r.bold ? 700 : 400 }}>{r.label}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 15, fontWeight: 700, color: r.color }}>
              {fmt(r.price)}
            </span>
            {r.isEntry && onSetAlert && (
              <button
                onClick={() => onSetAlert(r.price, isLong ? 'below' : 'above')}
                title="Set price alert at entry"
                style={{
                  background: 'var(--accent-dim)', border: '1px solid var(--accent)',
                  borderRadius: 6, padding: '3px 8px', cursor: 'pointer',
                  fontSize: 11, fontWeight: 700, color: 'var(--accent)',
                  display: 'flex', alignItems: 'center', gap: 4,
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--accent)'; e.currentTarget.style.color = '#fff'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'var(--accent-dim)'; e.currentTarget.style.color = 'var(--accent)'; }}
              >
                🔔 Alert
              </button>
            )}
          </div>
        </div>
      ))}
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 14px 4px', fontSize: 12, color: 'var(--text-muted)' }}>
        <span>1R (Risk) = {fmtR(riskR)}</span>
        <span>R:R = 1:2 & 1:3</span>
        <span>Risk = {setup.riskPercentage?.toFixed(1)}% of entry</span>
      </div>
    </div>
  );
}

function SetupCard({ setup, direction, isRecommended, onSetAlert }) {
  const isLong = direction === 'long';
  const accentColor = isLong ? 'var(--green)' : 'var(--red)';
  const accentDim = isLong ? 'var(--green-dim)' : 'var(--red-dim)';
  const label = isLong ? '↑ Long Setup' : '↓ Short Setup';

  return (
    <div className="card" style={{
      borderLeft: `4px solid ${accentColor}`,
      position: 'relative', overflow: 'hidden',
    }}>
      {isRecommended && (
        <div style={{
          position: 'absolute', top: 12, right: 12,
          fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
          padding: '2px 8px', borderRadius: 4,
          background: accentDim, color: accentColor, border: `1px solid ${accentColor}`,
        }}>★ RECOMMENDED</div>
      )}
      <div style={{ fontSize: 14, fontWeight: 700, color: accentColor, marginBottom: 14 }}>{label}</div>

      <PriceLadder setup={setup} direction={direction} onSetAlert={onSetAlert} />

      {setup.rationale && (
        <div style={{
          marginTop: 14, padding: '10px 14px',
          background: 'var(--bg-surface)', borderRadius: 8,
          fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.65,
          borderLeft: `2px solid ${accentColor}44`,
        }}>
          {setup.rationale}
        </div>
      )}
    </div>
  );
}

export default function TradingTab({ symbol, technicals, onAlertSaved }) {
  const [setup, setSetup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [alertPrice, setAlertPrice] = useState(null);
  const [alertCondition, setAlertCondition] = useState('below');

  const handleSetAlert = (price, condition) => {
    setAlertPrice(price);
    setAlertCondition(condition);
  };

  useEffect(() => {
    if (!symbol) return;
    setSetup(null); setError(null);
    api.get(`/ai/trading/${symbol}`)
      .then(r => { if (r.data) setSetup(r.data); })
      .catch(() => {});
  }, [symbol]);

  const generate = async (force = false) => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.post('/ai/trading', { symbol, force, lang: getLang() });
      setSetup(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate trading setup.');
    } finally { setLoading(false); }
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)' }}>
            {symbol} · Trading Setup
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
            Momentum style · 1R stop loss · 2R & 3R targets
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {setup?._meta && (
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              Generated {setup._meta.ageHours}h ago
            </span>
          )}
          {setup && (
            <button className="btn btn-ghost" onClick={() => generate(true)} disabled={loading} style={{ fontSize: 12 }}>
              ↺ Refresh
            </button>
          )}
          {!setup && !loading && (
            <button className="btn btn-primary" onClick={() => generate(false)}>
              ⚡ Generate Setup
            </button>
          )}
        </div>
      </div>

      {error && <div className="error-msg">{error}</div>}

      {/* Empty state */}
      {!setup && !loading && !error && (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--text-muted)', fontSize: 14 }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📊</div>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>No trading setup generated yet</div>
          <div style={{ fontSize: 13, marginBottom: 20, maxWidth: 400, margin: '0 auto 20px' }}>
            Click "Generate Setup" to get an AI-powered Long & Short trade plan with entry, stop loss, and take profit levels using the 1R/2R/3R risk/reward framework.
          </div>
          <button className="btn btn-primary" onClick={() => generate(false)}>⚡ Generate Setup</button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="card" style={{ padding: '40px 24px' }}>
          {[100, 60, 80, 40, 70].map((w, i) => (
            <div key={i}>
              <div className="skeleton" style={{ height: 16, width: `${w}%`, marginBottom: 8, borderRadius: 4 }} />
              <div className="skeleton" style={{ height: 6, width: '100%', marginBottom: 16, borderRadius: 3 }} />
            </div>
          ))}
          <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 12, marginTop: 8 }}>
            Analysing chart & generating 1R/2R/3R setup…
          </div>
        </div>
      )}

      {/* Results */}
      {setup && !loading && (
        <div>
          {/* Verdict */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 14 }}>
              <RecommendationBadge
                recommendation={setup.recommendation}
                confidence={setup.confidence}
                suitableForTrade={setup.suitableForTrade}
              />
            </div>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.75, margin: 0 }}>
              {setup.verdict}
            </p>
            {setup.riskWarning && (
              <div style={{
                marginTop: 12, padding: '10px 14px', borderRadius: 8,
                background: 'rgba(251,191,36,0.1)', border: '1px solid var(--yellow)',
                fontSize: 12, color: 'var(--yellow)',
              }}>
                ⚠ {setup.riskWarning}
              </div>
            )}
          </div>

          {/* Key Levels */}
          {setup.keyLevels && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-title">Key Levels</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--green)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>↑ Support</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {(setup.keyLevels.support || []).map((p, i) => (
                      <span key={i} style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 13, fontWeight: 600, padding: '4px 10px', borderRadius: 6, background: 'var(--green-dim)', color: 'var(--green)' }}>{fmt(p)}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--red)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>↓ Resistance</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {(setup.keyLevels.resistance || []).map((p, i) => (
                      <span key={i} style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 13, fontWeight: 600, padding: '4px 10px', borderRadius: 6, background: 'var(--red-dim)', color: 'var(--red)' }}>{fmt(p)}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Long + Short side by side */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {setup.long && (
              <SetupCard
                setup={setup.long}
                direction="long"
                isRecommended={setup.recommendation === 'long'}
                onSetAlert={handleSetAlert}
              />
            )}
            {setup.short && (
              <SetupCard
                setup={setup.short}
                direction="short"
                isRecommended={setup.recommendation === 'short'}
                onSetAlert={handleSetAlert}
              />
            )}
          </div>

          {/* Disclaimer */}
          <div style={{ marginTop: 16, fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', lineHeight: 1.6 }}>
            This is AI-generated analysis for educational purposes only and does not constitute financial advice.
            Always manage your own risk and consult a licensed financial advisor before trading.
          </div>
        </div>
      )}

      {alertPrice != null && (
        <SetAlertModal
          symbol={symbol}
          name={symbol}
          currentPrice={alertPrice}
          onClose={() => setAlertPrice(null)}
          onSaved={() => { setAlertPrice(null); onAlertSaved?.(); }}
          defaultCondition={alertCondition}
        />
      )}
    </div>
  );
}
